import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { staffApi, inr } from '../team/staffApi';

interface Pricing {
  basePrice: number;
  levels: Array<{ levelId: string; role: string; name: string; price: number | null }>;
  overrides: Array<{ staffId: string; name: string; price: number }>;
}

const ROLE_LABEL: Record<string, string> = { trainer: 'Trainers', therapist: 'Therapists', consultant: 'Consultants' };

/**
 * Price by level for an offering members buy with a chosen coach: a Senior's
 * PT plan can cost more than a Coach's. Blank = the base price. Individual
 * overrides (a star coach) are set on that person's Services tab and listed
 * here for reference.
 */
export default function OfferingPricingEditor({ ownerType, ownerId }: { ownerType: 'plan' | 'service'; ownerId: string }) {
  const [data, setData] = useState<Pricing | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = () =>
    staffApi<Pricing>(`/offering-pricing/${ownerType}/${ownerId}`)
      .then(p => { setData(p); setDraft(Object.fromEntries(p.levels.map(l => [l.levelId, l.price != null ? String(l.price) : '']))); })
      .catch(e => toast.error(e.message));
  useEffect(() => { load(); }, [ownerType, ownerId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="h-20 rounded-xl bg-gray-100 animate-pulse" />;
  if (data.levels.length === 0) {
    return <p className="text-[11px] text-gray-500">Every coach charges the base price ({inr(data.basePrice)}). To price by seniority, add levels in Team → Levels.</p>;
  }

  const roles = [...new Set(data.levels.map(l => l.role))];
  const dirty = data.levels.some(l => (l.price != null ? String(l.price) : '') !== (draft[l.levelId] ?? ''));

  const save = async () => {
    setSaving(true);
    try {
      await staffApi(`/offering-pricing/${ownerType}/${ownerId}`, {
        method: 'PUT',
        body: { levels: data.levels.map(l => ({ levelId: l.levelId, price: draft[l.levelId] === '' ? null : Number(draft[l.levelId]) })) },
      });
      toast.success('Level prices saved');
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {roles.map(role => (
        <div key={role} className="space-y-1.5">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{ROLE_LABEL[role] ?? role}</div>
          {data.levels.filter(l => l.role === role).map(l => (
            <div key={l.levelId} className="flex items-center justify-between gap-3">
              <span className="text-sm font-bold text-gray-700 truncate">{l.name}</span>
              <input
                type="number" min={0} placeholder={String(data.basePrice)}
                value={draft[l.levelId] ?? ''}
                onChange={e => setDraft(d => ({ ...d, [l.levelId]: e.target.value }))}
                className="w-32 px-3 py-2 border border-gray-200 rounded-xl text-sm text-right tabular-nums outline-none focus:border-indigo-400"
              />
            </div>
          ))}
        </div>
      ))}
      {data.overrides.length > 0 && (
        <p className="text-[11px] text-gray-500">
          Own prices: {data.overrides.map(o => `${o.name} ${inr(o.price)}`).join(' · ')}
        </p>
      )}
      <button type="button" onClick={save} disabled={!dirty || saving} className="px-4 py-2 rounded-xl bg-gray-900 text-white text-xs font-black disabled:opacity-40">
        {saving ? 'Saving…' : 'Save level prices'}
      </button>
    </div>
  );
}
